// Modo pantalla: la misma vista para una pantalla de sala, sin barra ni menú,
// la tabla ajustada a la pantalla, y sin que el equipo apague la pantalla
// (etapa-1 § 4.1.3).
//
// Se prende por URL (`/#/main/navegacion/entrada/<id>?pantalla=1`) y queda
// prendido en la pestaña (sessionStorage): sobrevive a recargas y al paso por el
// login, que pierde los query params. `?pantalla=0` lo apaga. También se entra
// y se sale con los botones de la entrada (`entrarModoPantalla` / `salir…`).

const CLAVE = 'modo-pantalla';

/** En una pantalla de sala no hay a quién pedirle nada: sin diálogos ni avisos. */
export function enModoPantalla(): boolean {
  return document.body.classList.contains('modo-pantalla');
}

export function iniciarModoPantalla(): void {
  if (!leer()) return;
  document.body.classList.add('modo-pantalla');
  mantenerEncendida();
  marcarQuieta();
  // Al recargar no hay gesto del usuario y el navegador no deja pedir pantalla
  // completa: se pide con el primer toque (o F11 / kiosk del equipo).
  document.addEventListener(
    'pointerdown',
    () => document.documentElement.requestFullscreen?.().catch(() => undefined),
    { once: true },
  );
}

/** Recarga en modo pantalla: los componentes lo leen al crearse. */
export function entrarModoPantalla(): void {
  try {
    sessionStorage.setItem(CLAVE, '1');
  } catch {
    // Sin storage no queda guardado: va por la URL.
    history.replaceState(null, '', conPantalla('1'));
  }
  location.reload();
}

export function salirModoPantalla(): void {
  try {
    sessionStorage.removeItem(CLAVE);
  } catch {
    /* sin storage, alcanza con la URL */
  }
  if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  // Si entró por URL, el `?pantalla=1` lo volvería a prender al recargar.
  history.replaceState(null, '', conPantalla(null));
  location.reload();
}

function conPantalla(valor: string | null): string {
  const [ruta, query = ''] = location.hash.split('?');
  const q = new URLSearchParams(query);
  if (valor) q.set('pantalla', valor);
  else q.delete('pantalla');
  const resto = q.toString();
  return `${location.pathname}${location.search}${ruta}${resto ? `?${resto}` : ''}`;
}

// Sin uso por 5 s, `pantalla-quieta` en el body: los estilos esconden los
// controles y el cursor. Vuelven con cualquier uso.
function marcarQuieta(): void {
  let espera: ReturnType<typeof setTimeout>;
  const usar = () => {
    document.body.classList.remove('pantalla-quieta');
    clearTimeout(espera);
    espera = setTimeout(
      () => document.body.classList.add('pantalla-quieta'),
      5000,
    );
  };
  for (const ev of ['pointermove', 'pointerdown', 'keydown', 'wheel'])
    document.addEventListener(ev, usar, { passive: true });
  usar();
}

function leer(): boolean {
  // Rutas con hash: los params van después del `#/ruta?`; también se aceptan antes.
  const q = new URLSearchParams(location.hash.split('?')[1] ?? location.search);
  try {
    if (q.get('pantalla') === '0') sessionStorage.removeItem(CLAVE);
    else if (q.get('pantalla') === '1') sessionStorage.setItem(CLAVE, '1');
    return sessionStorage.getItem(CLAVE) === '1';
  } catch {
    // Sin storage, vale sólo lo que dice la URL de esta carga.
    return q.get('pantalla') === '1';
  }
}

// El navegador suelta el wake lock cada vez que la pestaña se oculta: se vuelve
// a pedir al volver. Sin soporte (o sin HTTPS) la pantalla queda como la tenga
// configurada el equipo.
function mantenerEncendida(): void {
  const pedir = () =>
    (navigator as any).wakeLock?.request('screen').catch(() => undefined);
  pedir();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') pedir();
  });
}
